import { CostForecastQueryDatasourceFactory } from '@root/Services/Invoices/CostForecastService';
import { QueryDatasource, QueryDatasourceMetadata } from '@root/Services/Query/QueryDatasource';
import { SchemaService } from '@root/Services/QueryExpr';
import { inject, injectable } from 'tsyringe';
import { describeDatasource } from '../../Components/DatasourceDescriptor';
import { BaseDatasourcePresetsBuilder, IDatasourcePresetsBuilderFactory, INotificationPresetItem } from './BasePresets';

class CostForecastDatasourcePresetsBuilder extends BaseDatasourcePresetsBuilder {
    public constructor(queryDatasource: QueryDatasource, schemaSvc: SchemaService) {
        super(queryDatasource, schemaSvc);
    }

    public getPresets() {
        if (!this.schemaCtx.schemaSvc.types.length) {
            return [];
        }
        const { name, icon: Icon } = describeDatasource(this.baseQueryDatasource);
        return this.createDatasourcePresets(name, <Icon />, [this.buildCustomAlert()], [this.buildCustomReport()]);
    }

    private buildCustomAlert(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();

        return this.buildCustomAlertPreset((data) =>
            this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                    QueryOptions: { Parameters: {}, Filters: [], DatasourceName: this.baseQueryDatasource.name },
                    Trigger: { Threshold: {} },
                },
                data
            )
        );
    }

    private buildCustomReport(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();

        return this.buildCustomReportPreset((data) =>
            this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                    QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
                    Trigger: this.defaultSchedTrigger('Weekly'),
                },
                data
            )
        );
    }
}

@injectable()
export class CostForecastDatasourcePresetsBuilderFactory implements IDatasourcePresetsBuilderFactory {
    public constructor(@inject(CostForecastQueryDatasourceFactory) private readonly fcDatasourceFactory: CostForecastQueryDatasourceFactory) {}

    public getSupportedDatasources(): QueryDatasourceMetadata[] {
        return [this.fcDatasourceFactory.getMetadata()];
    }

    public async get(_: string) {
        const queryDatasource = await this.fcDatasourceFactory.getDatasource();
        const types = await queryDatasource.schema.getSchema();
        const schemaSvc = SchemaService.create(types);
        return new CostForecastDatasourcePresetsBuilder(queryDatasource, schemaSvc);
    }
}
